import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheckIcon, QrCodeIcon, ClipboardDocumentIcon, CheckCircleIcon } from '@heroicons/react/24/outline';


const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || `\${API_BASE_URL}`;

interface TwoFactorSetup {
  qrCodeUrl: string;
  secret: string;
  backupCodes: string[];
}

const TwoFactorSetupPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [setup, setSetup] = useState<TwoFactorSetup | null>(null);
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showBackup, setShowBackup] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchSetup();
  }, []);

  const fetchSetup = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`\${API_BASE_URL}/api/settings/two-factor/setup`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSetup(data.setup);
      }
    } catch (error) {
      console.error('Failed to load 2FA setup:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setVerifying(true);
    setError('');
    setSuccess(false);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`\${API_BASE_URL}/api/settings/two-factor/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ code }),
      });
      if (res.ok) {
        setSuccess(true);
        setShowBackup(true);
      } else {
        const data = await res.json();
        setError(data.message || 'Verification failed. Please check the code and try again.');
      }
    } catch (err) {
      setError('Verification failed. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const handleCopyBackup = async () => {
    if (!setup) return;
    try {
      await navigator.clipboard.writeText(setup.backupCodes.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy backup codes:', error);
    }
  };

  if (loading || !setup) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate('/dashboard/settings/security')}
        className="text-sm text-indigo-600 hover:text-indigo-500 mb-4"
      >
        ← Back to Security Settings
      </button>
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Two-Factor Authentication Setup</h1>
      <p className="mb-6 text-sm text-gray-600">
        Scan the QR code with your authenticator app (Google Authenticator, Authy, etc.) and enter the 6-digit code to enable 2FA.
      </p>
      
      {!showBackup ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col items-center">
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <img src={setup.qrCodeUrl} alt="2FA QR Code" className="w-48 h-48" />
            </div>
            <div className="text-center mb-4">
              <p className="text-xs text-gray-500 mb-1">Manual entry code:</p>
              <code className="text-sm font-mono bg-gray-100 px-3 py-1 rounded">{setup.secret}</code>
            </div>
            <form onSubmit={handleVerify} className="w-full max-w-xs space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter 6-digit code from your app
                </label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="block w-full text-center text-2xl tracking-widest rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  required
                  maxLength={6}
                  pattern="\\d{6}"
                />
              </div>
              {error && (
                <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">{error}</div>
              )}
              <button
                type="submit"
                disabled={verifying || code.length !== 6}
                className="w-full px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {verifying ? 'Verifying...' : 'Enable 2FA'}
              </button>
            </form>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <div className="flex items-center mb-2">
              <CheckCircleIcon className="w-6 h-6 text-green-600 mr-2" />
              <span className="text-green-900 font-medium">2FA Successfully Enabled!</span>
            </div>
            <p className="text-sm text-green-700">
              Your account is now protected with two-factor authentication.
            </p>
          </div>
          
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <ShieldCheckIcon className="w-6 h-6 text-yellow-600 mr-2" />
              <span className="text-yellow-900 font-medium">Save Your Backup Codes</span>
            </div>
            <p className="text-sm text-yellow-700 mb-4">
              Store these backup codes in a safe place. You can use them to access your account if you lose your authenticator device.
            </p>
            <div className="bg-white rounded-md p-4 mb-4">
              <ul className="grid grid-cols-2 gap-2">
                {setup.backupCodes.map((backupCode, index) => (
                  <li key={index} className="font-mono text-sm text-gray-900">
                    {backupCode}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleCopyBackup}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-yellow-900 bg-yellow-100 hover:bg-yellow-200"
              >
                <ClipboardDocumentIcon className="w-5 h-5 mr-2" />
                {copied ? 'Copied!' : 'Copy All Codes'}
              </button>
              <button
                onClick={() => navigate('/dashboard/settings/security')}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TwoFactorSetupPage;
